import { Component, HostBinding, OnInit, signal } from '@angular/core';
import { StoreService } from '@services/store.service';

import { finalize, Observable } from 'rxjs';

import { AccessTokenState } from '@models/api/access-token-state';

import { BaseLogging } from './base-logging';
import { HttpApiError } from '@models/api/http-api-error';
import { Person } from '@models/users/person';
import { SM } from '@models/base/helper-types';
import { PrefsState } from '@models/states/prefs-state';
import { Org } from '@models/users/org';

@Component({
    template: '',
})
export class BaseComponent extends BaseLogging implements OnInit {
    @HostBinding('class.layout')     _hostLayout: boolean = true;
    @HostBinding('class.fit')           _hostFit: boolean = false;
    @HostBinding('class.flex')         _hostFlex: boolean = false;
    @HostBinding('class.wrap')         _hostWrap: boolean = false;
    @HostBinding('class.relative') _hostRelative: boolean = false;
    @HostBinding('class.vertical') _hostVertical: boolean = false;
    @HostBinding('class.scroll-x')  _hostScrollX: boolean = false;
    @HostBinding('class.scroll-y')  _hostScrollY: boolean = false;

    readonly _lError         = signal<HttpApiError | undefined>(undefined);

    public error?: HttpApiError | HttpApiError[];
    public lError?: HttpApiError;
    public loading: boolean = true;
    public sending: boolean = false;
    public isGuest?: boolean;
    public userId?: string;
    public user?: Person;
    public activeOrgId?: string;
    public activeOrg?: Org;
    public pref: SM<any> = {};
    public granted: SM<boolean> = {};
    public pGranted: SM<SM<boolean>> = {};
    public isAuthUp?: boolean;
    private __prefs?: SM<{ id: string, v: any }>;

    protected initDone: boolean = false;

    constructor(protected _store?: StoreService | null) {
        super();

        if (_store) {
            this._S['__base.user'] = _store.state('user').subscribe(state => {
                this.userId = state.userId;
                this.user = state.user;
                this.isGuest = state.isGuest;
            });
            this.activeOrgId = _store.activeOrgId.getValue();
            this.activeOrg = this.activeOrgId ? _store.getStateDirect('orgs').items[this.activeOrgId] : undefined;
            this._orgId.set(this.activeOrgId);
            this._S['__base.granted'] = _store.granted.subscribe(granted => {
                this.granted = granted.global;
                this.pGranted = granted;
            });
            this._S['__activeOrg'] = _store.onActiveOrgChange.subscribe(id => {
                this.activeOrgId = id;
                this._orgId.set(this.activeOrgId);
                this.activeOrg = this.activeOrgId ? _store.getStateDirect('orgs').items[this.activeOrgId] : undefined;
                if (this.activeOrgId) {
                    this.vzOnActiveOrgChange();
                }
            });
            // this._S['__activeOrg'] = _store.state('orgs').pipe(skip(1)).subscribe(st => {
            //     const org = this.activeOrgId ? st.items[this.activeOrgId] : undefined;
            //     if (org) {
            //         this.activeOrg = org;
            //     }
            // });
            this._S['__authState'] = _store.authTokenState.subscribe(ats => {
                if (ats == AccessTokenState.Good) {
                    if (this.isAuthUp === false) {
                        this.isAuthUp = true;
                        this.vzOnAuthUp();
                    }
                    this.isAuthUp = true;
                }
                else if (ats == AccessTokenState.ExpiredErrorTemp || ats == AccessTokenState.ExpiredErrorTempNetwork) {
                    if (this.isAuthUp === true) {
                        this.isAuthUp = false;
                        this.vzOnAuthDown();
                    }
                    this.isAuthUp = false;
                }
            });
        }
    }

    ngOnInit(): void {
        this.initDone = true;
    }

    vzOnAuthUp(): void {}
    vzOnAuthDown(): void {}
    vzOnActiveOrgChange(): void {}

    __initPrefs(prefs: SM<keyof PrefsState>, opts?: { ucb?: (id: string, v: string) => void }): void {
        this.__U(['__cmp_prefs']);
        if (!this._store) {
            return;
        }
        this.pref = {};
        this.__prefs = {};
        const p: any = this._store.getState('prefs');
        Object.keys(prefs).forEach((k: string) => {
            this.__prefs![k] = { id: (prefs as any)[k], v: p[(prefs as any)[k]] };
            Object.defineProperty(this.pref, k, {
                set: x => {
                    if (this.__prefs![k].v != x) {
                        this.__prefs![k].v = x;
                        this._store?.patchState('prefs', { [this.__prefs![k].id]: x });
                        // console.log('__initPrefs.pref.set', `[${this.__prefs![k].id}]:`, x);
                        this.__detectChanges();
                    }
                },
                get: () => this.__prefs![k].v
            });
        });
        this._S['__cmp_prefs'] = this._store.state('prefs').subscribe((st: any) => {
            Object.keys(this.__prefs!).forEach((k: string) => {
                if (st[this.__prefs![k].id] != this.__prefs![k].v) {
                    this.__prefs![k].v = st[this.__prefs![k].id];
                    if (opts?.ucb) {
                        opts.ucb(k, st[this.__prefs![k].id]);
                    }
                    this.__detectChanges();
                }
            });
        });
    }

    __error(err: HttpApiError | HttpApiError[], show = true): void {
        this.error = undefined;
        this.loading = false;
        this.__detectChanges();
        if (this._store && this._store.authTokenState.getValue() >= AccessTokenState.ExpiredErrorTemp) {
            return;
        }
        setTimeout(() => {
            if (show) {
                this.error = err;
            }
            this.lError = Array.isArray(err) ? err[err.length - 1] : err;
            this.__detectChanges();
        });
    }

    __loading<T>(obs: Observable<T>, opts?: { cb?: () => void }): Observable<T> {
        this.loading = true;
        this.__detectChanges();
        return obs.pipe(finalize(() => {
            this.loading = false;
            this.__detectChanges();
            if (opts?.cb) {
                opts?.cb();
            }
        }));
    }

    __sending<T>(obs: Observable<T>, opts?: { cb?: () => void }): Observable<T> {
        this.sending = true;
        this.__detectChanges();
        return obs.pipe(finalize(() => {
            this.sending = false;
            this.__detectChanges();
            if (opts?.cb) {
                opts?.cb();
            }
        }));
    }

    __finalize<T>(obs: Observable<T>, cb: () => void): Observable<T> {
        this.__detectChanges();
        return obs.pipe(finalize(() => {
            this.__detectChanges();
            cb();
        }));
    }

    __detectChanges(): void {
        if (this.__cdr) {
            if (this.initDone) {
                this.__cdr.detectChanges();
            }
            else {
                this.__cdr.markForCheck();
            }
        }
    }

}
